/**
 * An HL7 visit row as returned by `GET /v1/subjects/visits?patientId=`.
 *
 * The endpoint deletes every null key before responding, so only [id] is
 * guaranteed to be present on a row.
 */
export interface Visit {
  id: number;
  visit_number?: string;
  visit_point_of_care?: string;
  visit_point_of_care_description?: string;
  visit_clinic_name?: string;
  visit_clinic_type_code?: string;
  visit_hospital_service?: string;
  visit_facility?: string;
  visit_facility_id?: number;
  visit_consultant_doctor?: string;
  visit_consultant_doctor_last_name?: string;
  visit_consultant_doctor_first_name?: string;
  visit_consultant_doctor_prefix?: string;
  visit_referring_doctor?: string;
  visit_referring_doctor_last_name?: string;
  visit_referring_doctor_first_name?: string;
  visit_referring_doctor_prefix?: string;
  visit_admit_date_time?: string;
  visit_discharge_date_time?: string;
  visit_patient_class?: string;
  visit_patient_type?: string;
  completed?: boolean;
  task_array?: number[];
}

export type FormattedVisit = Visit & {
  location: string | null;
  consultant: string | null;
  referrer: string | null;
  hasTasks: boolean;
  clinicCode: string | null;
  type: string | null;
  visitNumber: string | null;
  admitDateTime: string | null;
  dischargeDateTime: string | null;
};

export function visitLocation(visit?: Visit | null): string | null {
  if (!visit) return null;
  return visit.visit_point_of_care_description?.trim()
    || visit.visit_clinic_name?.trim()
    || null;
}

export function formatVisitDoctor(
  parts: { last?: string; first?: string; prefix?: string; flat?: string }
): string | null {
  const last = parts.last?.trim();
  const first = parts.first?.trim();
  const prefix = parts.prefix?.trim();
  const name = [last, first].filter(Boolean).join(', ');
  if (!name) return parts.flat?.trim() || null;
  return prefix ? `${name} (${prefix})` : name;
}

function visitConsultant(visit: Visit): string | null {
  return formatVisitDoctor({
    last: visit.visit_consultant_doctor_last_name,
    first: visit.visit_consultant_doctor_first_name,
    prefix: visit.visit_consultant_doctor_prefix,
    flat: visit.visit_consultant_doctor,
  });
}

function visitReferrer(visit: Visit): string | null {
  return formatVisitDoctor({
    last: visit.visit_referring_doctor_last_name,
    first: visit.visit_referring_doctor_first_name,
    prefix: visit.visit_referring_doctor_prefix,
    flat: visit.visit_referring_doctor,
  });
}

function visitHasTasks(visit: Visit): boolean {
  return (visit.task_array?.length ?? 0) > 0 || visit.completed === true;
}

function visitClinicCode(visit: Visit): string | null {
  return visit.visit_clinic_type_code?.trim() || visit.visit_point_of_care?.trim() || null;
}

function visitType(visit: Visit): string | null {
  return visit.visit_patient_class?.trim() || visit.visit_patient_type?.trim() || null;
}

export function formatVisit(visit: Visit): FormattedVisit {
  return {
    ...visit,
    location: visitLocation(visit),
    consultant: visitConsultant(visit),
    referrer: visitReferrer(visit),
    hasTasks: visitHasTasks(visit),
    clinicCode: visitClinicCode(visit),
    type: visitType(visit),
    visitNumber: visit.visit_number?.trim() || null,
    admitDateTime: visit.visit_admit_date_time || null,
    dischargeDateTime: visit.visit_discharge_date_time || null,
  };
}
